import { firebase } from "../config/configureFirebase";
import { update, get } from "firebase/database";
import { BOOKINGS_ACTIVE_CHECK_LIMIT } from "../store/types";
import { resolveRequestTimeoutSec } from "./requestTimeout";
import { isWomenOnlyCarType, normalizeGender } from "./womenOnlyService";
import { isRoundTrip, applyRoundTripToEstimate, reverseRouteCoords, reverseWaypoints } from "./roundTrip";
import { isRoadPolyline } from "./liveRoute";

export const formatBookingObject = async (bookingData, settings) => {
  const c = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const reference = [...Array(6)].map(_ => c[~~(Math.random()*c.length)]).join('');
  
  const { config, appcat } = firebase;
  const cat = appcat || 'bidcab';
  let today;
  try{
      let res =  await fetch(`https://${config.projectId}.web.app/getservertime`, { method: 'GET', headers: {'Content-Type': 'application/json'}});
      const json = await res.json();
      if(json.time){
        today = json.time;
      } else{
        today = new Date().getTime();
      }
  }catch (err){
    today = new Date().getTime();
  }

  let pickUp = { lat: bookingData.pickup.coords.lat, lng: bookingData.pickup.coords.lng, add: bookingData.pickup.description };
  let drop = { lat: bookingData.drop.coords.lat, lng: bookingData.drop.coords.lng, add: bookingData.drop.description };
  
  let coords = [{latitude: pickUp.lat,longitude: pickUp.lng}];
  if(bookingData.drop.waypointsStr){
      bookingData.drop.waypointsStr.split("|").forEach((point)=>{
          if (!point || !point.includes(',')) return;
          let lat = parseFloat(point.split(',')[0]);
          let lng = parseFloat(point.split(',')[1]);
          if (isNaN(lat) || isNaN(lng)) return;
          coords.push({latitude: lat, longitude: lng});
      });
  } else if (Array.isArray(bookingData.drop.waypoints) && bookingData.drop.waypoints.length > 0) {
      // Fallback si hay paradas sin waypointsStr
      bookingData.drop.waypoints.forEach((p) => {
          const lat = parseFloat(p?.lat);
          const lng = parseFloat(p?.lng);
          if (isNaN(lat) || isNaN(lng)) return;
          coords.push({ latitude: lat, longitude: lng });
      });
  }
  coords.push({latitude: drop.lat,longitude: drop.lng});

  var otp;
  if(bookingData.settings.otp_secure)
      otp = Math.floor(Math.random() * 90000) + 10000;
  else{
      otp = false;
  }
  const requestTimeoutSec = resolveRequestTimeoutSec(settings);
  const requestExpiresAt = bookingData.bookLater
      ? null
      : today + requestTimeoutSec * 1000;
  const roundTrip = isRoundTrip(bookingData.roundTrip);
  // Premium (Uber/inDrive): solo guardar polilínea de calle. No persistir recta A→B.
  let routeCoords = null;
  const estimatePoly = bookingData.estimate && Array.isArray(bookingData.estimate.waypoints)
    ? bookingData.estimate.waypoints.filter((p) => p && p.latitude != null && p.longitude != null)
    : null;
  if (isRoadPolyline(estimatePoly)) {
    routeCoords = estimatePoly;
  } else if (isRoadPolyline(coords)) {
    routeCoords = coords;
  }
  const returnCoords = roundTrip && routeCoords ? reverseRouteCoords(routeCoords) : null;
  const bookingWaypoints = Array.isArray(bookingData.drop.waypoints) && bookingData.drop.waypoints.length > 0
      ? bookingData.drop.waypoints.filter(
          (p) => p && p.lat != null && p.lng != null && !isNaN(Number(p.lat)) && !isNaN(Number(p.lng))
        )
      : null;
  // Multiparada + ida/vuelta: en el regreso se visitan las mismas paradas en orden inverso.
  const returnWaypoints = roundTrip && bookingWaypoints && bookingWaypoints.length
      ? reverseWaypoints(bookingWaypoints)
      : null;
  const estimate = applyRoundTripToEstimate(bookingData.estimate, roundTrip);
  const estimateFare = parseFloat(estimate.estimateFare) || 0;
  const customerOfferRaw = bookingData.customer_offer != null
      ? parseFloat(bookingData.customer_offer)
      : (bookingData.offerFare != null ? parseFloat(bookingData.offerFare) : 0);
  const customerOffer = (customerOfferRaw > 0) ? customerOfferRaw : estimateFare;
  const payableBase = cat === 'bidcab'
      ? (customerOfferRaw > 0 ? customerOffer : estimateFare)
      : estimateFare;
  const commissionType = bookingData.carDetails.convenience_fee_type || 'percentage';
  let commissionRate = parseFloat(bookingData.carDetails.convenience_fees);
  if (!Number.isFinite(commissionRate) || commissionRate < 0) commissionRate = 0;
  // Recalcular comisión sobre el monto a pagar (oferta o estimado), no confiar solo en estimate.convenience_fees
  let convenienceFees = commissionType === 'flat'
      ? commissionRate
      : (payableBase * commissionRate) / 100;
  if (!Number.isFinite(convenienceFees) || convenienceFees < 0) {
    convenienceFees = parseFloat(estimate.convenience_fees) || 0;
  }
  const tripCostForShare = payableBase > 0 ? payableBase : estimateFare;
  return {
      carType: bookingData.carDetails.name,
      carImage: bookingData.carDetails.image,
      womenOnly: isWomenOnlyCarType(bookingData.carDetails),
      customer_gender: normalizeGender(bookingData.userDetails.gender),
      customer: bookingData.userDetails.uid,
      commission_type: commissionType,
      commission_rate: commissionRate,
      reference: reference,
      customer_email: bookingData.userDetails.email,
      customer_name: bookingData.userDetails.firstName + ' ' + bookingData.userDetails.lastName,
      customer_contact: bookingData.userDetails.mobile? bookingData.userDetails.mobile: ' ',
      customer_token: bookingData.userDetails.pushToken? bookingData.userDetails.pushToken: ' ',
      customer_image: bookingData.userDetails.profile_image ? bookingData.userDetails.profile_image : "",
      // Insignia para el conductor: pasajero con documento ID aprobado
      customer_verified: !!(
        bookingData.userDetails.verifyIdStatus === 'approved' ||
        (bookingData.userDetails.verifyIdApproved && bookingData.userDetails.verifyIdStatus !== 'rejected')
      ),
      drop: drop,
      pickup: pickUp,
      estimate: cat === "bidcab" ? (customerOfferRaw > 0 ? customerOfferRaw : estimateFare) : estimateFare,
      // Tarifa del sistema (FareCalculator), sin sustituir por la oferta del cliente.
      system_estimate: estimateFare > 0 ? estimateFare : null,
      // Mínimo que el pasajero podía ofertar (MapScreen / bidprice). Piso del conductor.
      customer_min_offer: (() => {
        const m = bookingData.customer_min_offer != null
          ? parseFloat(bookingData.customer_min_offer)
          : (bookingData.minimumPrice != null ? parseFloat(bookingData.minimumPrice) : NaN);
        return Number.isFinite(m) && m > 0 ? m : null;
      })(),
      estimateDistance: estimate.estimateDistance,
      distance: estimate.estimateDistance,
      estimateTime: estimate.estimateTime,
      status: cat === "bidcab" ? "NEW" : bookingData.booking_type_admin || (cat === "delivery" ? bookingData.deliveryWithBid : !settings.prepaid) || bookingData.booking_type_fleetadmin ? "NEW" : "PAYMENT_PENDING",
      bookLater:bookingData.bookLater,
      tripdate: bookingData.bookLater?bookingData.tripdate:today,
      bookingDate: today,
      requestTimeoutSec: bookingData.bookLater ? null : requestTimeoutSec,
      requestExpiresAt,
      otp: otp,
      booking_type_admin:bookingData.booking_type_admin,
      coords: routeCoords,
      returnCoords: returnCoords,
      waypoints: bookingWaypoints,
      returnWaypoints: returnWaypoints,
      tripInstructions: bookingData.tripInstructions? bookingData.tripInstructions: null,
      trip_cost: tripCostForShare,
      convenience_fees: convenienceFees,
      driver_share: (tripCostForShare - convenienceFees).toFixed(settings.decimal),
      fleet_admin_comission: bookingData.carDetails.fleet_admin_fee ? bookingData.carDetails.fleet_admin_fee : null,
      paymentPacket: bookingData.paymentPacket? bookingData.paymentPacket : null,
      preRequestedDrivers: cat === "bidcab" ? null : bookingData.preRequestedDrivers?  bookingData.preRequestedDrivers: null,
      requestedDrivers: bookingData.requestedDrivers?  bookingData.requestedDrivers: null,
      driverEstimates: bookingData.driverEstimates?  bookingData.driverEstimates: null,
      ...bookingData.instructionData,
      roundTrip: roundTrip || null,
      roundTripLeg: roundTrip ? 'outbound' : null,
      fleetadmin: bookingData.fleetadmin? bookingData.fleetadmin:null,
      payment_mode: bookingData.payment_mode,
      booking_from_web: bookingData.booking_from_web? bookingData.booking_from_web: false,
      deliveryWithBid: cat === "delivery" ? bookingData.deliveryWithBid ? bookingData.deliveryWithBid : false : null,
      customer_offer: customerOffer,
      booking_type_fleetadmin: cat === "bidcab" ? null : bookingData.booking_type_fleetadmin,
      cityId: bookingData.cityId || null,
      cityName: bookingData.cityName || null,
  }
}

export const addActualsToBooking = async (booking, address, driverLocation) => {
  const end_time = new Date();
  const diff = (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
  const totalTimeTaken = Math.abs(Math.round(diff));
  booking.trip_end_time = end_time.getHours() + ":" + end_time.getMinutes() + ":" + end_time.getSeconds();
  booking.endTime = end_time.getTime();
  booking.total_trip_time = totalTimeTaken;
  return booking;
};

export const updateDriverQueue = (booking) => {
  const { singleUserRef, appcat } = firebase;
  const cat = appcat || 'bidcab';
  if (cat === "bidcab") {
    if(booking.status === 'NEW' && booking.driver && booking.prepaid){
      booking.status = "ACCEPTED";
    }
    // Solo marcar queue si ya hay conductor asignado (evita write a users/undefined
    // al cerrar el modal "Reserva exitosa" en status NEW sin driver).
    if (booking.driver) {
      update(singleUserRef(booking.driver),{ queue: true });
    }
  }
  return booking;
};

// MEJORA URGENTE 3: Función helper para verificar si un conductor tiene bookings activos
export const checkDriverActiveBookings = async (driverId) => {
  const {
    bookingListRef
  } = firebase;
  
  try {
    // Estados activos que requieren queue=true
    const activeStatuses = ['NEW', 'ACCEPTED', 'PICKUP_USER', 'ARRIVED', 'STARTED', 'REACHED', 'PENDING', 'PAYMENT_PENDING'];
    
    // Recent trips only — active rides are never buried deep in history
    const bookingsSnapshot = await get(
      bookingListRef(driverId, 'driver', BOOKINGS_ACTIVE_CHECK_LIMIT)
    );
    
    if (!bookingsSnapshot.exists()) {
      return false; // No hay bookings, puede cambiar queue a false
    }
    
    const bookings = bookingsSnapshot.val();
    
    // Verificar si hay algún booking con status activo (excepto el booking actual que estamos procesando)
    for (let bookingId in bookings) {
      const booking = bookings[bookingId];
      if (activeStatuses.includes(booking.status)) {
        return true; // Hay booking activo, mantener queue=true
      }
    }
    
    return false; // No hay bookings activos, puede cambiar queue a false
  } catch (error) {
    console.error('Error checking driver active bookings:', error);
    // En caso de error, por seguridad mantener queue=true
    return true;
  }
};

export const driverQueue = (firebase.appcat || 'bidcab') === "delivery" ? true : false;
